import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AddressLookupService as msAddressLookupService } from '@yol-digital/ms-client';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { ENVIRONMENT_URLS_CONFIG_TOKEN } from 'app-initialization';
import Api = msAddressLookupService.Api;

@Injectable({ providedIn: 'root' })
export class AddressLookupService {
  private httpClient = inject(HttpClient);
  private config = inject(ENVIRONMENT_URLS_CONFIG_TOKEN);
  private api = new Api(this.config.newMicroServiceEndpoint, this.httpClient);
  public addressFormValidations$ = new BehaviorSubject(false);

  public findCities(postcode: string, isLineCheck = true): Observable<string[]> {
    if (!postcode || postcode.length !== 4) return of([]);

    let params;
    params = {
      postcode,
    };

    if (isLineCheck) {
      params = {
        ...params,
        lookupMethod: 'linecheck',
      };
    }

    return this.api.publicV1.cityV1(params).pipe(
      shareReplay(1),
      map((resp: msAddressLookupService.AddressLookupResp) => resp.cities)
    );
  }

  public findStreetNames(
    postcode: string,
    city: string,
    streetPrefix: string,
    isLineCheck = true
  ): Observable<msAddressLookupService.StreetNameAndId[]> {
    if (!postcode || postcode.length !== 4) return undefined;

    let params;
    params = {
      postcode,
      city,
      streetName: streetPrefix,
    };

    if (isLineCheck) {
      params = {
        ...params,
        lookupMethod: 'linecheck',
      };
    }
    return this.api.publicV1.streetV1(params).pipe(
      shareReplay(1),
      map((resp: msAddressLookupService.StreetNameAndId[]) => resp as msAddressLookupService.StreetNameAndId[])
    );
  }

  public findStreetNumbers(
    postcode: string,
    city: string,
    street: string,
    streetNumberPrefix: string,
    isLineCheck = true
  ): Observable<string[]> {
    if (!postcode || postcode.length !== 4) return of([]);

    const baseParams = {
      postcode,
      city,
      streetNumber: streetNumberPrefix,
      streetName: street,
    };

    let streetNumberV1Call$ = this.api.publicV1.streetNumberV1(baseParams);

    if (isLineCheck) {
      streetNumberV1Call$ = this.findStreetNames(postcode, city, street).pipe(
        switchMap((streetNames: msAddressLookupService.StreetNameAndId[]) => {
          //TODO: Return to implementation considering only one matching street after Nute fixes the bug where they return several entries
          // (with different streetIds) per street name
          const matchingStreets = streetNames?.filter(streetResponse => street === streetResponse.streetName);

          if (!matchingStreets || matchingStreets.length === 0) {
            return of({ streetNumbers: [] });
          }

          return forkJoin(
            matchingStreets.map(matchingStreet => {
              const params: msAddressLookupService.AddressLookupStreetNumberReqV1 = {
                ...baseParams,
                lookupMethod: 'linecheck',
                streetId: matchingStreet?.streetId?.toString(),
              };

              return this.api.publicV1.streetNumberV1(params).pipe(catchError(() => of({ streetNumbers: [] })));
            })
          ).pipe(
            map((responses: msAddressLookupService.AddressLookupResp[]) => ({
              streetNumbers: responses
                .map(r => r.streetNumbers)
                .flat()
                .sort((a, b) => a.localeCompare(b)),
            }))
          );
        })
      );
    }

    return streetNumberV1Call$.pipe(
      shareReplay(1),
      map((resp: msAddressLookupService.AddressLookupResp) => resp.streetNumbers)
    );
  }
}
